






























































































































import { Component, Vue, Emit, Watch, Prop } from "vue-property-decorator";
import rest from "@/rest";
import { namespace } from "vuex-class";
import { ExerciseVM } from "@/interfaces/ExerciseVM";
import { CourseElementVm, CustomTranslationType, QuizOptionVm } from "@/interfaces/CourseElementVm";
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { ParamChoiceAiSuggestionVm } from "@/interfaces/ParamChoiceAiSuggestionVm";
import { CcPageVm } from "@/interfaces/CcPageVm";
import { List } from "linq-collections";
import { QuizOptionWithQuestionVm } from "@/interfaces/QuizOptionWithQuestionVm";
import { CustomizationVm } from "@/interfaces/CustomizationVm";

const globals = namespace("globals");
const customization = namespace("customization");

@Component({
  components: {},
})
export default class SingleChoiceElement extends Vue {
  @Prop() element!: CourseElementVm;

  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedExercise!: ExerciseVM;
  @globals.Getter modulePages!: CcPageVm[];
  @globals.Getter selectedModulePage!: CcPageVm | null;
  @globals.Getter isMasterLanguageCode!: boolean;
  @globals.Action refreshSelectedBundle!: () => Promise<void>;

  isBusy = false;
  showMarkdownEditor = false;
  showEditDialog = false;
  editText = '';
  clipboardCopied = false;

  @Watch('showEditDialog')
  onShowEditDialogChanged(val: boolean): void {
    if (val) {
      this.$nextTick(() => {
        const ta = this.$refs.editTextarea as any;
        if (ta && ta.calculateInputHeight) {
          ta.calculateInputHeight();
        }
      });
    }
  }

  // Computed property to handle single selection radio logic
  get correctOptionId(): number {
    const correctOption = this.element.options?.find(opt => opt.isRight);
    return correctOption ? correctOption.id : -1;
  }

  // Set the correct option when radio button is changed
  set correctOptionId(optionId: number) {
    if (this.element.options) {
      // Set all options to false first
      this.element.options.forEach(opt => {
        opt.isRight = (opt.id === optionId);
      });

      // Make sure Vue reactivity is triggered
      this.$forceUpdate();
    }
  }

  mounted() {
    // Initialize the single choice - make sure at least one option is selected if available
    if (this.element.options && this.element.options.length > 0) {
      const hasCorrect = this.element.options.some(opt => opt.isRight);
      if (!hasCorrect) {
        this.element.options[0].isRight = true;
      }
    }
  }

  /**
   * Adds a new option to the single choice element
   */
  private addQuizOption(): void {
    // Calculate new option ID based on highest existing ID + 1
    const maxId = this.element.options && this.element.options.length > 0
      ? Math.max(...this.element.options.map(opt => opt.id))
      : 0;

    const newOptionId = maxId + 1;

    const newOption: QuizOptionVm = {
      id: newOptionId,
      guid: this.$globalHelper.getNewGuid(),
      customTranslation: CustomTranslationType.Master,
      title: '',
      text: '',
      resolution: '',
      isRight: false, // New options are incorrect by default
      contentUrl: '', // Not relevant for single choice, but we include it for consistency
      fillContainer: false, // Not relevant for single choice, but we include it for consistency
      horizontalAlign: 'end', // Not relevant for single choice, but we include it for consistency
      prompt: '', // Not relevant for single choice, but we include it for consistency
      changePrompt: '', // Not relevant for single choice, but we include it for consistency
      altText: '', // Not relevant for single choice, but we include it for consistency
      imageLibrarySearchText: '' // Not relevant for single choice, but we include it for consistency
    };

    // If this is the first option, mark it as correct by default
    if (!this.element.options || this.element.options.length === 0) {
      newOption.isRight = true;
    }

    if (!this.element.options) {
      this.element.options = [];
    }

    this.element.options.push(newOption);
    this.element.guid = this.$globalHelper.getNewGuid(); // Forces L3RN online to ignore previous saved element states!
  }

  /**
   * Removes an option from the single choice element
   * @param elementId - The ID of the element containing options
   * @param optionId - The ID of the option to remove
   */
  private removeQuizOption(elementId: number, optionId: number): void {
    if (!this.element.options) return;

    // Check if we're removing the correct option
    const removingCorrectOption = this.element.options.find(opt => opt.id === optionId)?.isRight;

    // Filter out the option with the specified ID
    this.element.options = this.element.options.filter(option => option.id !== optionId);

    // If we removed the correct option, set the first remaining option as correct
    if (removingCorrectOption && this.element.options.length > 0) {
      this.element.options[0].isRight = true;
    }

    this.element.guid = this.$globalHelper.getNewGuid(); // Forces L3RN online to ignore previous saved element states!

    // Force UI update
    this.$forceUpdate();
  }

  private async copyEditTextToClipboard(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.editText);
      this.clipboardCopied = true;
      await this.$globalHelper.delay(2000);
      this.clipboardCopied = false;
    } catch (e) {
      console.error('Clipboard copy failed', e);
    }
  }

  /**
   * Opens the plain-text edit dialog and populates editText from the current element state.
   */
  private openEditDialog(): void {
    const lines: string[] = [];
    if (this.element.text && this.element.text.trim() !== '') {
      lines.push(this.element.text + '\n');
    }

    (this.element.options ?? []).forEach(opt => {
      if (opt.text && opt.text.trim() !== '') {
        lines.push((opt.text ?? '') + (opt.isRight ? '*' : ''));
      }
    });
    this.editText = lines.join('\n');
    this.showEditDialog = true;
  }

  /**
   * Parses editText and applies the result back to the element.
   * Format: first line = question, subsequent lines = options (trailing * = correct).
   */
  private applyEditText(): void {
    const lines = this.editText.split('\n');
    if (lines.length === 0) return;

    // First line is the question
    this.element.text = lines[0].trim();

    // Remaining non-empty lines are options
    const optionLines = lines.slice(1).filter(l => l.trim() !== '');
    const newOptions: QuizOptionVm[] = optionLines.map((line, index) => {
      const isRight = line.trimEnd().endsWith('*');
      const text = isRight ? line.trimEnd().slice(0, -1).trimEnd() : line.trim();
      // Reuse existing option ID if available, otherwise generate a new one
      const existingId = this.element.options && this.element.options[index]
        ? this.element.options[index].id
        : index + 1;
      return {
        id: existingId,
        guid: this.$globalHelper.getNewGuid(),
        customTranslation: CustomTranslationType.Master,
        text,
        isRight,
      } as QuizOptionVm;
    });

    // Reassign sequential IDs
    newOptions.forEach((opt, i) => {
      opt.id = i + 1;
    });

    // console.log("Parsed options from edit text:", newOptions);

    // Ensure exactly one correct option (first marked wins, or first as fallback)
    const correctCount = newOptions.filter(o => o.isRight).length;
    if (correctCount === 0 && newOptions.length > 0) {
      newOptions[0].isRight = true;
    } else if (correctCount > 1) {
      let found = false;
      newOptions.forEach(o => {
        if (o.isRight) {
          if (found) o.isRight = false;
          else found = true;
        }
      });
    }

    this.element.options = newOptions;
    this.element.guid = this.$globalHelper.getNewGuid();
    this.showEditDialog = false;
    this.$forceUpdate();
  }

  private selectionChanged(): void {
    this.element.guid = this.$globalHelper.getNewGuid(); // Forces L3RN online to ignore previous saved element states!
  }

  private clearAndFocus(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.element) return;

    // Clear the textarea value
    this.element.text = '';
    // this.element.guid = this.$globalHelper.getNewGuid();

    // Focus the rendered textarea/input inside the v-textarea
    this.$nextTick(() => {
      const root = this.$el as HTMLElement;
      const textarea = root.querySelector('textarea') as HTMLTextAreaElement | null;
      if (textarea) {
        textarea.focus();
        return;
      }
      const input = root.querySelector('input') as HTMLInputElement | null;
      if (input) input.focus();
    });
  }

  /**
   * Gets AI suggestions for single choice options
   * @param element - The course element to get suggestions for
   */
  async getAiSuggestion(): Promise<void> {
    this.isBusy = true;

    try {
      // let params: ParamDictionary = { dictionary: {}};
      // params.dictionary!["BundleId"] = this.selectedExercise.exBundleId!.toString();
      // params.dictionary!["ExerciseId"] = this.selectedExercise.id!.toString();
      // params.dictionary!["Question"] = this.element.text!;

      let knownAnswers = new List(this.element.options!)
        .where((o) => (o.text?.trim() ?? '') !== '')
        .select((o) => o.text)
        .toArray()
        .join(", ");

      let knownQuestions = new List(this.modulePages)
        .where((page) => page.id !== this.selectedModulePage!.id)
        .selectMany((page) => page.courseElements ?? [])
        .where((el) => el.type === "SingleChoice" || el.type === "MultipleChoice")
        .select((el) => el.text)
        .toArray()
        .join(", ");

      // params.dictionary!["KnownQuestions"] = knownQuestions.join(", ");
      let params: ParamChoiceAiSuggestionVm = {
        page: this.selectedModulePage!,
        question: this.element.text || "",
        knownQuestions:  knownQuestions,
        knownAnswers: knownAnswers
      };

      const response = await rest.url("contentManager/getSingleChoiceAiSuggestion").post(params);

      if (response) {
        console.log("AI response", response);
        // remove empty options
        this.element.options = this.element.options!.filter(option => (option.text?.trim() ?? '') !== '');

        // For each new option, add an QuizOptionWithQuestionVm object to the element's options
        if (response && Array.isArray(response)) {
          this.element.text = response[0].question; // Set the question text from the first option
          if (!this.element.options) this.element.options = [];

          response.forEach((option, index) => {
            this.element.options!.push(<QuizOptionVm>{
              id: index + 1,
              guid: this.$globalHelper.getNewGuid(),
              title: '',
              text: option.text,
              isRight: option.isRight
            });
          });

          // Reassign IDs to ensure they're unique and sequential
          this.element.options.forEach((option, index) => {
            option.id = index + 1;
          });


          // Ensure single choice behavior - only one option is marked as correct
          let foundCorrect = false;
          this.element.options.forEach(opt => {
            if (opt.isRight) {
              if (foundCorrect) {
                opt.isRight = false;
              } else {
                foundCorrect = true;
              }
            }
          });

          // If no option is marked as correct, mark the first one
          if (!foundCorrect && this.element.options.length > 0) {
            this.element.options[0].isRight = true;
          }

          this.element.guid = this.$globalHelper.getNewGuid(); // Forces L3RN online to ignore previous saved element states!

          // Force UI update
          // await this.refreshSelectedBundle();
          this.$forceUpdate();
        }
      }
    } catch (error) {
      console.error("Error getting AI suggestions:", error);
    } finally {
      this.isBusy = false;
    }
  }
}
